// Single source of truth for what's shipped and what's coming.
// Surfaced in AboutScene; keep it ordered newest-first within each
// status group. Append a new entry when a slice ships.
//
// Only the latest shipped entry carries a `released` date — testers
// use it to gauge how recently the game was updated. Older shipped
// entries omit the date on purpose (it would otherwise look like
// the entire game was built in one month).
//
// Format: MM.DD.YY (e.g., '05.09.26').

export type ReleaseStatus = 'shipped' | 'planned';

export interface Release {
  readonly version: string;
  readonly codename: string;
  readonly status: ReleaseStatus;
  /**
   * Override for the version string shown in the about page headline.
   * When set, replaces the default `v${version}` rendering — used to
   * surface a range (e.g. "v0.8 → v0.8.2") when consecutive patch
   * releases get consolidated into one entry. `version` itself still
   * tracks the latest concrete release in the series (must match
   * APP_VERSION from package.json for the "current build" lookup).
   */
  readonly versionDisplay?: string;
  /**
   * MM.DD.YY shipping date. Set ONLY on the most recent shipped
   * release so the about page can surface "how up-to-date is this
   * game" without making the whole roadmap look month-old.
   */
  readonly released?: string;
  readonly highlights: readonly string[];
}

export const CHANGELOG: readonly Release[] = [
  // --- Shipped, newest first ---
  {
    version: '0.9.9',
    codename: 'Hourglass',
    status: 'shipped',
    released: '05.23.26',
    highlights: [
      '24-hour async turn timer — every game now has a fixed 24-hour window per turn so a player who walks away can\'t freeze the game.',
      'Auto-skip if you don\'t play in time: forfeit your reinforcements for the round; three skips in a row forfeits the game.',
      'Heads-up email at the 6-hour mark, then a soft toast + battle-log note when you next open a skipped game.',
    ],
  },
  {
    version: '0.9.8',
    codename: 'Beacon',
    status: 'shipped',
    highlights: [
      'Invite by username with held-seat reservation, branded invite email, and an INVITED card in the inbox.',
    ],
  },
  {
    version: '0.9.7',
    codename: 'Boxscore',
    status: 'shipped',
    highlights: [
      'Post-game stats with per-player dice-fairness charts and combat win rates.',
    ],
  },
  {
    version: '0.9.6',
    codename: 'Quickdraw',
    status: 'shipped',
    highlights: [
      'Snappier multiplayer with optimistic UI dispatch, plus a game-switch responsiveness fix.',
    ],
  },
  {
    version: '0.9.5',
    codename: 'Open Net',
    status: 'shipped',
    highlights: [
      'Open Games browser plus auto-start-on-full lobbies.',
    ],
  },
  {
    version: '0.9.4',
    codename: 'Shipmates',
    status: 'shipped',
    highlights: [
      'Friends system plus Discord and Reddit community links.',
    ],
  },
  {
    version: '0.9.3',
    codename: 'Whistle',
    status: 'shipped',
    highlights: [
      'Inactivity-based turn-reminder emails, plus a beta diagnostic snapshot in Settings.',
    ],
  },
  {
    version: '0.9.2',
    codename: 'Switchboard',
    status: 'shipped',
    highlights: [
      'Cross-account multiplayer reliability fix, plus an email-notification toggle and reset-password flow in Settings.',
    ],
  },
  {
    version: '0.9.1',
    codename: 'Foghorn',
    status: 'shipped',
    highlights: [
      'Email notifications for game start and end, plus account self-service in Settings.',
    ],
  },
  {
    version: '0.9.0',
    codename: "Crow's Nest",
    status: 'shipped',
    highlights: [
      'My Games inbox plus a modernized app chrome with persistent bottom navigation.',
    ],
  },
  {
    version: '0.8.6',
    codename: 'Shipshape',
    status: 'shipped',
    highlights: [
      'Branded confirmation emails + sharper link previews; App Store prep tail closed.',
    ],
  },
  {
    version: '0.8.5',
    codename: 'Crew Manifest',
    status: 'shipped',
    highlights: [
      'Real accounts: email + password sign-up, usernames, custom battleshark.online domain, PWA install.',
    ],
  },
  // The 0.8.x line through 0.8.2 was the multiplayer foundation:
  // 0.8.0 (Open Waters) shipped MP, 0.8.1 (Friend Trials) wrapped
  // a beta-hosting flow + server-side AI around it, 0.8.2 (Sea Legs)
  // sanded down the testing friction. Consolidated as one recap entry.
  {
    version: '0.8.0',
    codename: 'Open Waters',
    status: 'shipped',
    highlights: [
      'Online multiplayer foundation: share-link sessions, real-time sync, beta hosting, server-side AI.',
    ],
  },
  // --- Past versions: condensed to single-line summaries so the
  // current release reads as the page's headline. Only the latest
  // shipped entry carries detailed bullets; everything older is a
  // recap. Full bullet histories live in TODO.md + git history.
  {
    version: '0.7.0',
    codename: 'War Room',
    status: 'shipped',
    highlights: ['Pre-game lobby, team play (2v2 / 3v1), full battle log.'],
  },
  {
    version: '0.6.0',
    codename: 'Cartographer',
    status: 'shipped',
    highlights: ['Two new maps: Slash (15 tiles) and Terra (25 tiles).'],
  },
  {
    version: '0.5.0',
    codename: 'Tactician',
    status: 'shipped',
    highlights: ['Smarter AI with three personality archetypes.'],
  },
  {
    version: '0.4.0',
    codename: 'Reefkeeper',
    status: 'shipped',
    highlights: ['Region control bonuses + team rings on held regions.'],
  },
  {
    version: '0.3.0',
    codename: 'Lasthold',
    status: 'shipped',
    highlights: ['Win / lose modals + EV-based Risk-dice AI.'],
  },
  {
    version: '0.2.0',
    codename: 'Drydock',
    status: 'shipped',
    highlights: ['Tactical palette, compass map, dice tray, combat VFX.'],
  },
  {
    version: '0.1.0',
    codename: 'First Tides',
    status: 'shipped',
    highlights: ['Foundation: combat math, three-phase turns, end-to-end AI.'],
  },

  // --- Planned, in roadmap order ---
  {
    version: '1.0.0',
    codename: 'Sharknado',
    status: 'planned',
    highlights: [
      'Open web beta. Friends, invites, live and async turn timers, replays, stats, opt-in audio.',
    ],
  },
  {
    version: '2.0.0',
    codename: 'Pocket Sharks',
    status: 'planned',
    highlights: [
      'iOS and Android App Store launch. Google and Apple sign-in plus full security hardening.',
    ],
  },
];
